/**
 * Feedback Pipeline — API layer
 * Bug reports and improvement suggestions.
 */
import { API_URL, apiFetch, getActiveTenantSlug } from '../../api';

const BASE = `${API_URL}/api/v1/feedback`;

async function readError(res, fallback) {
  try {
    const body = await res.json();
    return body?.detail || fallback;
  } catch {
    return fallback;
  }
}

/* ── Create ── */
export async function createFeedback(data) {
  const res = await apiFetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte skicka feedback'));
  return res.json();
}

/* ── Upload attachment ── */
export async function uploadAttachment(feedbackId, file) {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await apiFetch(`${BASE}/${encodeURIComponent(feedbackId)}/attachments`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte ladda upp bilaga'));
  return res.json();
}

/* ── Attachment URL helper ── */
export function attachmentUrl(feedbackId, filename) {
  const url = `${BASE}/${encodeURIComponent(feedbackId)}/attachments/${encodeURIComponent(filename)}`;
  const slug = getActiveTenantSlug();
  if (!slug) return url;
  return `${url}?tenant_slug=${encodeURIComponent(slug)}`;
}

/* ── List ── */
export async function listFeedback({ status, type, module, severity, search, limit = 100, offset = 0 } = {}) {
  const p = new URLSearchParams();
  if (status) p.set('status', status);
  if (type) p.set('type', type);
  if (module) p.set('module', module);
  if (severity) p.set('severity', severity);
  if (search) p.set('search', search);
  p.set('limit', String(limit));
  p.set('offset', String(offset));
  const res = await apiFetch(`${BASE}?${p}`);
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte hämta feedback'));
  return res.json();
}

/* ── Stats ── */
export async function getFeedbackStats() {
  const res = await apiFetch(`${BASE}/stats`);
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte hämta statistik'));
  return res.json();
}

/* ── Get single ── */
export async function getFeedback(id) {
  const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte hämta feedback'));
  return res.json();
}

/* ── Update ── */
export async function updateFeedback(id, data) {
  const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte uppdatera feedback'));
  return res.json();
}

/* ── Icebox ── */
export async function moveToIcebox(id) {
  const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}/icebox`, { method: 'POST' });
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte flytta till icebox'));
  return res.json();
}

/* ── Reactivate ── */
export async function reactivateFeedback(id) {
  const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}/reactivate`, { method: 'POST' });
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte återaktivera'));
  return res.json();
}

/* ── Trigger analysis ── */
export async function triggerAnalysis(id) {
  const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}/analyze`, { method: 'POST' });
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte starta analys'));
  return res.json();
}

/* ── Submit agent result ── */
export async function submitAgentResult(id, data) {
  const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}/agent-result`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte skicka agentresultat'));
  return res.json();
}

/* ── Approve ── */
export async function approveFeedback(id) {
  const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}/approve`, { method: 'POST' });
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte godkänna'));
  return res.json();
}

/* ── Merge PR ── */
export async function mergeFeedbackPR(id) {
  const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}/merge`, { method: 'POST' });
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte merga PR'));
  return res.json();
}

/* ── Deploy ── */
export async function markDeployed(id) {
  const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}/deploy`, { method: 'POST' });
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte markera som deployad'));
  return res.json();
}

/* ── Activity log ── */
export async function getActivityLog(id) {
  const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}/activity-log`);
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte hämta aktivitetslogg'));
  return res.json();
}

/* ── History (KanAI Historik) ── */
export async function getFeedbackHistory({ page = 1, limit = 20, type, status } = {}) {
  const p = new URLSearchParams();
  p.set('page', String(page));
  p.set('limit', String(limit));
  if (type) p.set('type', type);
  if (status) p.set('status', status);
  const res = await apiFetch(`${BASE}/history?${p}`);
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte hämta historik'));
  return res.json();
}

/* ── Export as .md ── */
export async function exportFeedbackMd(id) {
  const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}/export`);
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte exportera'));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = id.replace(/\//g, '-') + '.md';
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Config: vilka kopplingar är aktiva (GitHub, KanAI) ── */
export async function getFeedbackConfig() {
  const res = await apiFetch(`${BASE}/config`);
  if (!res.ok) throw new Error(await readError(res, 'Kunde inte hämta inställningar'));
  return res.json();
}
